import { createHelpArticle } from '../helpModel'

export const postsContentArticles = [
  createHelpArticle({
    id: 'post-create', slug: 'create-a-post', title: 'Create a post', category: 'Posts & Content',
    description: 'Share a text or photo post from the Create flow and review its audience before publishing.',
    keywords: ['post', 'create', 'publish', 'photo', 'caption'],
    aliases: ['how to post', 'make a post', 'share a campus update', 'publish a photo post'],
    steps: [
      { title: 'Open Create', body: 'Choose Create in the app navigation, then choose Post.' },
      { title: 'Add text or photos', body: 'Write your caption and, if you want, select supported photos from your device.' },
      { title: 'Review the audience', body: 'Check the audience and other options shown in the composer.' },
      { title: 'Publish', body: 'Choose the post action in the composer and wait for its result.' },
    ],
    notes: ['The primary feed post composer is for text and photos. Use Clips for the primary video-posting workflow.'],
    relatedArticles: ['post-photos', 'post-audience', 'post-upload', 'clip-upload'],
    searchPriority: 3,
  }),
  createHelpArticle({
    id: 'post-delete', slug: 'delete-a-post', title: 'Delete a post', category: 'Posts & Content',
    description: 'Remove one of your own posts from its available post menu.',
    keywords: ['post', 'delete', 'remove', 'own content'],
    aliases: ['remove my post', 'take down my post', 'post disappeared'],
    steps: [
      { title: 'Open your post', body: 'Find the post on your profile or in a feed where it is visible.' },
      { title: 'Open the post menu', body: 'Choose the more-options menu on your own post.' },
      { title: 'Confirm deletion', body: 'Choose “Delete post” and confirm “Delete” in the confirmation dialog.' },
    ],
    notes: ['Only use the delete action for a post you own. Deletion is destructive.'],
    relatedArticles: ['post-create', 'report-post', 'post-edit'],
  }),
  createHelpArticle({
    id: 'post-photos', slug: 'add-photos-to-a-post', title: 'Add photos to a post', category: 'Posts & Content',
    description: 'Select photos from the media picker while composing a feed post.',
    keywords: ['photos', 'images', 'gallery', 'post', 'upload'],
    aliases: ['attach pictures to post', 'choose media for a post', 'photo post'],
    steps: [
      { title: 'Open the post composer', body: 'Choose Create, then Post.' },
      { title: 'Select photos', body: 'Use the composer media control to choose one or more photos from your device.' },
      { title: 'Review and publish', body: 'Check the selected media and audience, then publish when ready.' },
    ],
    notes: ['The composer accepts up to 10 selected media items. A video cannot be mixed with other selected media in this composer.'],
    troubleshooting: [{ problem: 'A photo is rejected.', solution: 'Check that it is an image type supported by the picker and within the displayed upload-size limit, then choose it again.' }],
    relatedArticles: ['post-create', 'post-upload', 'media-limits'],
  }),
  createHelpArticle({
    id: 'post-audience', slug: 'post-visibility', title: 'Choose who can see a post', category: 'Posts & Content',
    description: 'The post composer offers public, college, and friends audience options.',
    keywords: ['post visibility', 'audience', 'public', 'college', 'friends'],
    aliases: ['who can see my post', 'change post audience', 'friends only post', 'college-only post'],
    steps: [
      { title: 'Open the post composer', body: 'Choose Create, then Post.' },
      { title: 'Review visibility', body: 'Select the audience option shown in the composer: public, college, or friends.' },
      { title: 'Confirm before publishing', body: 'Check the selected audience before sharing the post.' },
    ],
    notes: ['An individual post audience is separate from whether your profile is public or private. Audience access can also depend on the account relationship and current app rules.'],
    relatedArticles: ['profile-visibility', 'post-create', 'privacy'],
  }),
  createHelpArticle({
    id: 'post-upload', slug: 'post-upload-failed-or-stuck', title: 'Post upload failed or is stuck', category: 'Posts & Content',
    description: 'Check the connection and selected media, then retry only through a control the composer displays.',
    keywords: ['upload failed', 'stuck', 'post', 'retry', 'network'],
    aliases: ['post not uploading', 'photo upload stuck', 'post failed to publish', 'upload spinning'],
    steps: [
      { title: 'Check your connection', body: 'Confirm the device is online before trying again.' },
      { title: 'Review the media selection', body: 'Remove an unsupported or over-limit file and keep video in Clips rather than mixing it into a photo post.' },
      { title: 'Follow the displayed result', body: 'If the composer shows a retry or error action, use it. Otherwise reopen the composer and select the media again.' },
    ],
    notes: ['Do not assume an unfinished post or upload was saved as a draft.'],
    troubleshooting: [{ problem: 'The same file fails again.', solution: 'Try a supported file within the current size limits and check your connection; there is no Help-specific technical-report form.' }],
    relatedArticles: ['post-photos', 'media-limits', 'clip-upload'],
    searchPriority: 4,
  }),
  createHelpArticle({
    id: 'post-save', slug: 'save-and-find-a-post', title: 'Save a post and find saved posts', category: 'Posts & Content',
    description: 'Use the save control on a post and open the Saved area from your profile.',
    keywords: ['save post', 'bookmark', 'saved', 'collection'],
    aliases: ['bookmark a post', 'where are my saved posts', 'find saved content'],
    steps: [
      { title: 'Save a post', body: 'Choose the save/bookmark action on the post.' },
      { title: 'Open Saved', body: 'Go to your Profile and open “Saved” to view saved items.' },
      { title: 'Remove a saved post', body: 'Use the save control again on the post to remove it from your saved items.' },
    ],
    relatedArticles: ['post-create', 'event-save'],
  }),
  createHelpArticle({
    id: 'post-engagement', slug: 'like-comment-and-reply', title: 'Like, comment, and reply to a post', category: 'Posts & Content',
    description: 'Use a post’s visible reaction and comment controls to engage with content.',
    keywords: ['like', 'comment', 'reply', 'post', 'engagement'],
    aliases: ['respond to a post', 'write a comment', 'reply to someone', 'react to post'],
    steps: [
      { title: 'Open the post', body: 'Find the post in the feed or on its author’s profile.' },
      { title: 'Like or comment', body: 'Use the like control or open Comments and write your comment.' },
      { title: 'Reply to a comment', body: 'Choose the reply action for the comment you want to answer and submit your response.' },
    ],
    notes: ['Comment submission may show a Community Guidelines warning or block. Follow the message shown; do not try to bypass moderation.'],
    relatedArticles: ['comment-edit', 'comment-warning', 'comment-review'],
  }),
  createHelpArticle({
    id: 'comment-edit', slug: 'edit-or-delete-my-comment', title: 'Edit or delete my comment', category: 'Posts & Content',
    description: 'Use the comment’s available actions to change or remove a comment you wrote.',
    keywords: ['edit comment', 'delete comment', 'remove reply'],
    aliases: ['change my comment', 'fix a typo in comment', 'remove my reply'],
    steps: [
      { title: 'Open the post comments', body: 'Open the post and choose its Comments control.' },
      { title: 'Choose your comment action', body: 'Use the available edit or delete action on a comment you wrote.' },
      { title: 'Save or confirm', body: 'Save the edited text, or confirm deletion if prompted.' },
    ],
    notes: ['The comment menu only provides actions for comments you are allowed to manage. A direct report-comment control was not verified.'],
    relatedArticles: ['post-engagement', 'comment-warning', 'content-appeal'],
  }),
  createHelpArticle({
    id: 'comment-warning', slug: 'comment-warning-before-posting', title: 'Why did my comment show a warning?', category: 'Posts & Content',
    description: 'Campnect may display a Community Guidelines warning and ask you to review a comment before proceeding.',
    keywords: ['comment warning', 'guidelines', 'moderation', 'draft'],
    aliases: ['comment flagged', 'warning before posting', 'comment may violate rules'],
    body: ['Read the warning and review your draft. If Campnect offers a confirmation step, decide whether to edit or proceed using that screen. A warning does not guarantee that a comment will be published or that it is approved.'],
    notes: ['Do not attempt to evade moderation or infer internal classifier rules.'],
    relatedArticles: ['comment-review', 'comment-edit', 'legal-guidelines'],
  }),
  createHelpArticle({
    id: 'comment-review', slug: 'comment-blocked-or-held-for-review', title: 'Why is my comment blocked or held?', category: 'Posts & Content',
    description: 'A blocked or held comment may not appear publicly; check the notice and any eligible appeal information shown in the app.',
    keywords: ['comment blocked', 'held for review', 'removed comment', 'appeal'],
    aliases: ['comment not visible', 'comment under review', 'my comment was removed'],
    body: ['Read the message shown when you submit or edit the comment. If an eligible moderation record is available, it may appear in Content appeals. An approved appeal does not publish the comment automatically; the exact draft must be resubmitted when the app allows it.'],
    relatedArticles: ['content-appeal', 'post-engagement', 'comment-warning'],
  }),
  createHelpArticle({
    id: 'post-edit', slug: 'edit-an-existing-post', title: 'Edit an existing post', category: 'Posts & Content',
    description: 'The post card currently exposes an Edit post action for posts you are allowed to edit.',
    keywords: ['edit post', 'change caption', 'post correction'],
    aliases: ['update my post', 'fix post text', 'change post audience'],
    steps: [
      { title: 'Open your post menu', body: 'On your post, open the more-options menu and select “Edit post” when it is available.' },
      { title: 'Change text or audience', body: 'Update the text or visibility in the edit sheet.' },
      { title: 'Save changes', body: 'Choose “Save changes” and wait for the update.' },
    ],
    notes: ['Editing is conditional on the post exposing the action. Do not assume that its media can be replaced from this edit sheet.'],
    relatedArticles: ['post-delete', 'post-audience', 'post-create'],
  }),
  createHelpArticle({
    id: 'media-limits', slug: 'image-and-video-upload-limits', title: 'Image and video size and duration limits', category: 'Posts & Content',
    description: 'The shared media policy lists a maximum of 25 MiB per image, 100 MiB per video, and 60 seconds for a video.',
    keywords: ['media', 'image limit', 'photo size', 'video limit', 'duration', 'upload size'],
    aliases: ['how large can a photo be', 'video file too large', 'maximum upload file size', 'video duration limit'],
    body: ['The shared upload policy allows images up to 25 MiB per image and video up to 100 MiB per video, with a maximum video duration of 60 seconds. The composer and media picker may impose additional surface-specific restrictions; follow the actual picker guidance.'],
    notes: ['The primary feed post composer supports text and photos; use Clips for the primary video-posting flow. A server-wide maximum is not proof that every individual screen accepts every file of that size.'],
    relatedArticles: ['post-photos', 'clip-limits', 'clip-upload-troubleshooting'],
  }),
]